/**
 * CommentBubble Component
 * Displays an AI-generated comment suggestion with action buttons.
 */
import React, { useState, useCallback } from 'react';
import type { Comment } from '../../types';
import ConfidenceIndicator from './ConfidenceIndicator';

interface CommentBubbleProps {
  comment: Comment;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onEdit: (id: string) => void;
  onDismissCategory?: (category: string) => void;
  isSelected?: boolean;
}

const CommentBubble: React.FC<CommentBubbleProps> = ({
  comment,
  onAccept,
  onReject,
  onEdit,
  onDismissCategory,
  isSelected = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [pressedButton, setPressedButton] = useState<string | null>(null);

  const handleButtonPress = useCallback((buttonName: string, action: () => void) => {
    setPressedButton(buttonName);
    // Remove the press state after animation completes
    setTimeout(() => setPressedButton(null), 150);
    action();
  }, []);

  const handleAccept = () => {
    handleButtonPress('accept', () => onAccept(comment.id));
  };

  const handleReject = () => {
    handleButtonPress('reject', () => onReject(comment.id));
  };

  const handleEdit = () => {
    handleButtonPress('edit', () => onEdit(comment.id));
  };

  const handleDismissCategory = () => {
    if (onDismissCategory) {
      handleButtonPress('dismiss', () => onDismissCategory(comment.category));
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-lg border ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
      } max-w-sm transition-all duration-200 animate-slide-in`}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 border-b border-gray-100 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-400 font-medium">
            p.{comment.pageNumber}
          </span>
          <ConfidenceIndicator level={comment.confidence} size="sm" />
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
            {comment.category}
          </span>
        </div>
        <button
          className="text-gray-400 hover:text-gray-600"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? '−' : '+'}
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-3 space-y-3">
          {/* Highlighted text */}
          <div className="text-xs text-gray-500 bg-yellow-50 p-2 rounded border-l-2 border-yellow-400">
            <span className="font-medium">Highlighted:</span>
            <p className="mt-1 italic">"{comment.highlightedText}"</p>
          </div>

          {/* Suggestion */}
          <p className="text-sm text-gray-700">{comment.suggestion}</p>

          {/* Action buttons */}
          <div className="flex items-center space-x-2 pt-2 border-t border-gray-100">
            <button
              onClick={handleAccept}
              aria-label="Accept"
              className={`flex-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 transition-all active:scale-95 ${
                pressedButton === 'accept' ? 'animate-button-press' : ''
              }`}
            >
              Accept
            </button>
            <button
              onClick={handleEdit}
              aria-label="Edit"
              className={`flex-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 transition-all active:scale-95 ${
                pressedButton === 'edit' ? 'animate-button-press' : ''
              }`}
            >
              Edit
            </button>
            <button
              onClick={handleReject}
              aria-label="Reject"
              className={`flex-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-all active:scale-95 ${
                pressedButton === 'reject' ? 'animate-button-press' : ''
              }`}
            >
              Reject
            </button>
          </div>

          {/* Category dismiss button */}
          {onDismissCategory && (
            <button
              onClick={handleDismissCategory}
              className={`w-full mt-2 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-all active:scale-95 ${
                pressedButton === 'dismiss' ? 'animate-button-press' : ''
              }`}
              title={`Dismiss all "${comment.category}" comments for this submission`}
            >
              Ignore all &quot;{comment.category}&quot;
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentBubble;
